import { InputHTMLAttributes, KeyboardEvent, ReactNode, useRef } from 'react';

/**
 * Design `.rad` (ExamEditor): a 22px circle, control-coloured ring, filled green with a white
 * inner ring when checked. Native radio input underneath.
 */
export default function Radio({ className = '', ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return (
    <input
      {...rest}
      type="radio"
      className={`h-[22px] w-[22px] flex-none cursor-pointer appearance-none rounded-full border-2 border-control bg-surface transition-colors hover:border-success checked:border-success checked:bg-success checked:shadow-[inset_0_0_0_4px_rgb(var(--yd-surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${className}`}
    />
  );
}

export interface RadioCardOption<K extends string> {
  key: K;
  label: ReactNode;
  hint?: ReactNode;
}

interface CardsProps<K extends string> {
  options: RadioCardOption<K>[];
  value: K;
  onChange: (key: K) => void;
  /** Accessible name of the radiogroup. */
  label: string;
  className?: string;
}

/**
 * Design `.opt` + `.rad` (AdminRagSetup): stacked option cards with an 18px ring; the chosen
 * card gets a navy 2px outline and a surface-2 fill, its ring a 5px navy border.
 * Keyboard: one Tab stop (the chosen card, else the first); arrow keys move and select.
 */
export function RadioCards<K extends string>({ options, value, onChange, label, className = '' }: CardsProps<K>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const selected = options.findIndex((o) => o.key === value);
  const tabStop = selected >= 0 ? selected : 0;
  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
    const jump = e.key === 'Home' ? 0 : e.key === 'End' ? options.length - 1 : null;
    if (!step && jump === null) return;
    e.preventDefault();
    const next = jump ?? (i + step + options.length) % options.length;
    onChange(options[next].key);
    refs.current[next]?.focus();
  }
  return (
    <div role="radiogroup" aria-label={label} className={`flex flex-col gap-2.5 ${className}`}>
      {options.map((o, i) => {
        const on = o.key === value;
        return (
          <button key={o.key} ref={(el) => { refs.current[i] = el; }} type="button" role="radio" aria-checked={on}
            tabIndex={i === tabStop ? 0 : -1} onKeyDown={(e) => onKeyDown(e, i)} onClick={() => onChange(o.key)}
            className={`flex w-full items-start gap-3 rounded-[14px] border px-4 py-3.5 text-left text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
              on ? 'border-navy bg-surface-2 shadow-[inset_0_0_0_1px_theme(colors.navy)] dark:border-ink dark:shadow-[inset_0_0_0_1px_rgb(var(--yd-ink))]' : 'border-line-strong bg-surface hover:border-line-hover'}`}>
            <span aria-hidden="true" className={`mt-0.5 h-[18px] w-[18px] flex-none rounded-full ${on ? 'border-[5px] border-navy dark:border-ink' : 'border-2 border-switch-off'}`} />
            <span className="min-w-0">
              <span className="block font-semibold">{o.label}</span>
              {o.hint && <span className="block text-[13px] text-muted">{o.hint}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
